import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getVisibleModules, getModuleHome, resolveModuleForPath, isPathActive, ALL_ITEMS } from "../../lib/navModules";
import { LayoutGrid, ChevronDown, Star, Boxes, Check } from "lucide-react";
import { cn } from "../../lib/utils";
import api from "../../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export default function ModuleMenuBar() {
  const { hasPermission, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [enabledModules, setEnabledModules] = useState(null);
  const [customModules, setCustomModules] = useState([]);
  const [pinned, setPinned] = useState(() => {
    try { return JSON.parse(localStorage.getItem("stancor_pinned_nav")) || []; } catch { return []; }
  });

  useEffect(() => {
    api.get("/modules/enabled").then(res => setEnabledModules(res.data.enabled || null)).catch(() => {});
    api.get("/module-builder/modules").then(res => setCustomModules((res.data || []).filter(m => m.enabled))).catch(() => {});
  }, []);

  const modules = useMemo(
    () => getVisibleModules(enabledModules, hasPermission, user),
    [enabledModules, hasPermission, user]
  );

  const resolved = resolveModuleForPath(location.pathname);
  const activeModule = resolved && resolved !== "__custom__"
    ? modules.find(m => m.key === resolved.key) || resolved
    : null;
  const activeCustom = resolved === "__custom__"
    ? customModules.find(m => location.pathname.startsWith(`/cm/${m.slug}`))
    : null;

  const togglePin = (path) => {
    setPinned(prev => {
      const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
      localStorage.setItem("stancor_pinned_nav", JSON.stringify(next));
      return next;
    });
  };

  const pinnedItems = pinned.map(p => ALL_ITEMS.find(it => it.path === p)).filter(Boolean);
  const currentPinned = pinned.includes(location.pathname);
  const ActiveIcon = activeModule?.icon;

  return (
    <nav className="flex items-center gap-0.5 min-w-0" data-testid="app-sidebar">
      {/* Apps launcher */}
      <button onClick={() => navigate("/")}
        title="All Apps"
        data-testid="apps-launcher-btn"
        className={cn(
          "h-9 w-9 shrink-0 rounded-md flex items-center justify-center transition-colors",
          location.pathname === "/" ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground/70"
        )}>
        <LayoutGrid className="w-[18px] h-[18px]" strokeWidth={1.8} />
      </button>

      {/* Module switcher */}
      {(activeModule || activeCustom) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="h-9 shrink-0 flex items-center gap-2 px-2.5 rounded-md hover:bg-muted transition-colors"
              data-testid="module-switcher-btn">
              {activeModule ? (
                <span className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: activeModule.color }}>
                  <ActiveIcon className="w-3.5 h-3.5 text-white" strokeWidth={1.8} />
                </span>
              ) : (
                <span className="w-6 h-6 rounded-md flex items-center justify-center bg-slate-700">
                  <Boxes className="w-3.5 h-3.5 text-white" strokeWidth={1.8} />
                </span>
              )}
              <span className="text-sm font-semibold tracking-tight max-w-[140px] truncate">
                {activeModule?.label || activeCustom?.name}
              </span>
              <ChevronDown className="w-3 h-3 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-60 rounded-md max-h-[70vh] overflow-y-auto">
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">Switch module</DropdownMenuLabel>
            {modules.map(m => {
              const MIcon = m.icon;
              return (
                <DropdownMenuItem key={m.key} onClick={() => navigate(getModuleHome(m))}
                  data-testid={`module-switch-${m.key}`}
                  className="gap-2.5 rounded-lg text-sm py-2">
                  <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: m.color }}>
                    <MIcon className="w-3.5 h-3.5 text-white" strokeWidth={1.8} />
                  </span>
                  <span className="flex-1">{m.label}</span>
                  {activeModule?.key === m.key && <Check className="w-3.5 h-3.5 text-primary" />}
                </DropdownMenuItem>
              );
            })}
            {customModules.length > 0 && <DropdownMenuSeparator />}
            {customModules.map(m => (
              <DropdownMenuItem key={m.id} onClick={() => navigate(`/cm/${m.slug}`)}
                className="gap-2.5 rounded-lg text-sm py-2">
                <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 bg-slate-700">
                  <Boxes className="w-3.5 h-3.5 text-white" strokeWidth={1.8} />
                </span>
                <span className="flex-1">{m.name}</span>
                {activeCustom?.id === m.id && <Check className="w-3.5 h-3.5 text-primary" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Section menus of active module */}
      {activeModule && (
        <div className="flex items-center gap-0.5 min-w-0 overflow-x-auto scrollbar-none" data-testid="module-menu-sections">
          {(activeModule.visibleSections || activeModule.sections).map(section => {
            const items = section.items;
            if (items.length === 0) return null;
            const sectionActive = items.some(it => isPathActive(location.pathname, it.path));

            if (items.length === 1) {
              const it = items[0];
              const active = isPathActive(location.pathname, it.path);
              return (
                <NavLink key={section.label} to={it.path}
                  data-testid={`menu-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={cn(
                    "h-9 px-3 shrink-0 flex items-center rounded-md text-[13px] font-medium transition-colors whitespace-nowrap",
                    active ? "bg-primary/10 text-primary" : "text-foreground/70 hover:bg-muted hover:text-foreground"
                  )}>
                  {it.label}
                </NavLink>
              );
            }

            return (
              <DropdownMenu key={section.label}>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid={`menu-section-${section.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={cn(
                      "h-9 px-3 shrink-0 flex items-center gap-1 rounded-md text-[13px] font-medium transition-colors whitespace-nowrap",
                      sectionActive ? "bg-primary/10 text-primary" : "text-foreground/70 hover:bg-muted hover:text-foreground"
                    )}>
                    {section.label}
                    <ChevronDown className="w-3 h-3 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 rounded-md">
                  {items.map(it => {
                    const IIcon = it.icon;
                    const active = isPathActive(location.pathname, it.path);
                    return (
                      <DropdownMenuItem key={it.path} onClick={() => navigate(it.path)}
                        data-testid={`menu-item-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={cn("gap-2.5 rounded-lg text-sm py-2", active && "bg-muted font-semibold")}>
                        <IIcon className="w-4 h-4 text-muted-foreground" strokeWidth={1.8} />
                        <span className="flex-1">{it.label}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); togglePin(it.path); }}
                          title={pinned.includes(it.path) ? "Unpin" : "Pin"}
                          className="p-0.5 opacity-40 hover:opacity-100">
                          <Star className={cn("w-3 h-3", pinned.includes(it.path) && "fill-amber-400 text-amber-400 opacity-100")} />
                        </button>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })}
        </div>
      )}

      {/* Pinned favorites */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            title="Pinned pages"
            data-testid="pinned-menu-btn"
            className="h-9 w-9 shrink-0 rounded-md flex items-center justify-center hover:bg-muted transition-colors">
            <Star className={cn("w-4 h-4", pinnedItems.length > 0 ? "fill-amber-400 text-amber-400" : "text-foreground/50")} strokeWidth={1.8} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-60 rounded-md">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">Pinned</DropdownMenuLabel>
          {pinnedItems.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">No pinned pages yet. Pin pages from module menus or pin the current page below.</p>
          )}
          {pinnedItems.map(it => {
            const IIcon = it.icon;
            return (
              <DropdownMenuItem key={it.path} onClick={() => navigate(it.path)}
                data-testid={`pinned-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="gap-2.5 rounded-lg text-sm py-2">
                <IIcon className="w-4 h-4 text-muted-foreground" strokeWidth={1.8} />
                <span className="flex-1">{it.label}</span>
                <button onClick={(e) => { e.stopPropagation(); togglePin(it.path); }} title="Unpin" className="p-0.5">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                </button>
              </DropdownMenuItem>
            );
          })}
          {location.pathname !== "/" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => togglePin(location.pathname)}
                data-testid="pin-current-page-btn"
                className="gap-2.5 rounded-lg text-sm py-2 text-muted-foreground">
                <Star className={cn("w-4 h-4", currentPinned && "fill-amber-400 text-amber-400")} />
                {currentPinned ? "Unpin this page" : "Pin this page"}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
}
